import styled, { keyframes } from 'styled-components';
import { DESKTOP_BREAKPOINT, DESKTOP_BREAKPOINT_NUMBER } from '../../../../styles/breakpoints';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { maxOnboardingContentWidth, radii, space } from '../../../../styles/onboardingUiTokens';
import { OnboardingBusyOverlay } from '../elements/OnboardingBusyOverlay';
import { OnboardingBody } from './OnboardingBody';
import { OnboardingFlowShell } from './OnboardingFlowShell';

const COURSE_DATA_LOADING_MESSAGES = ['Deine Kursdaten werden geladen.'] as const;

const mobileMax = `${DESKTOP_BREAKPOINT_NUMBER - 1}px`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
`;

const SkeletonBlock = styled.div<{ $width?: string; $height: string; $radius?: string }>`
  width: ${({ $width }) => $width ?? '100%'};
  height: ${({ $height }) => $height};
  border-radius: ${({ $radius }) => $radius ?? radii.md};
  background: ${colors.light};
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const HeaderRegion = styled.div`
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${mobileMax}) {
    position: sticky;
    top: 0;
    z-index: 10;
  }
`;

const HeaderInner = styled.div`
  width: 100%;
  min-width: 0;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    max-width: ${maxOnboardingContentWidth};
    margin: 0 auto;
    padding: ${space.md} ${space.lg} ${space.sm};
  }
`;

const TherapistCardSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: ${space.md};
  width: 100%;
  padding: ${space.xs} ${space.sm};
  margin-bottom: ${space.md};
  border-radius: ${radii.lg};
  background: color-mix(in srgb, ${colors.light} 45%, ${colors.white});
  box-shadow: 0 1px 2px ${getMediumRGBA(0.12)};
`;

const CardTextStack = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${space.xxs};
`;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
  padding-bottom: ${space.xl};
`;

const HeadingBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.sm};
`;

const ParagraphBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.xs};
`;

/** Loading state while persist hydrates or the signed-up patient doc resolves. */
export function OnboardingFlowLoadingScreen() {
  return (
    <OnboardingFlowShell>
      <HeaderRegion aria-hidden="true">
        <HeaderInner>
          <TherapistCardSkeleton>
            <SkeletonBlock $width="2.375rem" $height="2.375rem" $radius={radii.pill} />
            <CardTextStack>
              <SkeletonBlock $width="55%" $height="0.75rem" />
              <SkeletonBlock $width="40%" $height="0.625rem" />
              <SkeletonBlock $width="65%" $height="0.625rem" />
            </CardTextStack>
          </TherapistCardSkeleton>
          <SkeletonBlock $height="3px" $radius={radii.pill} />
        </HeaderInner>
      </HeaderRegion>
      <OnboardingBody disableSupportHint>
        <ContentColumn>
          <HeadingBars>
            <SkeletonBlock $width="95%" $height="1.25rem" />
            <SkeletonBlock $width="88%" $height="1.25rem" />
            <SkeletonBlock $width="72%" $height="1.25rem" />
          </HeadingBars>
          <ParagraphBars>
            <SkeletonBlock $height="0.875rem" />
            <SkeletonBlock $width="92%" $height="0.875rem" />
            <SkeletonBlock $width="78%" $height="0.875rem" />
          </ParagraphBars>
          <SkeletonBlock $height="12rem" $radius={radii.lg} />
        </ContentColumn>
      </OnboardingBody>
      <OnboardingBusyOverlay
        open
        showAfterMs={0}
        messages={COURSE_DATA_LOADING_MESSAGES}
        ariaLabel="Deine Kursdaten werden geladen."
      />
    </OnboardingFlowShell>
  );
}
